/**
 * Report export (spec §7.7–§7.8: XLSX is required in F1, CSV as the plain
 * text form). The sheet is built from the same columns the grid renders, so
 * the file cannot disagree with the screen. The XLSX writer is self-contained:
 * an OOXML package is a zip of XML parts and stored (uncompressed) entries are
 * a valid zip, so no dependency is added for six small parts.
 */

export interface ExportColumn<Row> {
  header: string;
  /** A plain decimal is written as a number cell; everything else as text. */
  numeric?: boolean;
  exportValue?(row: Row): string;
}

export interface ExportSheet<Row> {
  sheetName: string;
  columns: ReadonlyArray<ExportColumn<Row>>;
  rows: readonly Row[];
  /** The footer row, one string per column. */
  totals?: ReadonlyArray<string>;
}

export type ExportFormat = 'csv' | 'xlsx';

export function sheetOf<Row>(
  sheetName: string,
  columns: ReadonlyArray<ExportColumn<Row>>,
  rows: readonly Row[],
  totals?: ReadonlyArray<string>,
): ExportSheet<Row> {
  return { sheetName, columns, rows, totals };
}

const NUMERIC = /^-?\d+(?:\.\d+)?$/u;

export function isNumeric(value: string): boolean {
  return NUMERIC.test(value.trim());
}

export function exportFileName(sheetName: string, extension: ExportFormat): string {
  return `${sheetName}.${extension}`;
}

/* --------------------------------------------------------------------- csv */

function csvCell(value: string): string {
  return /[",\r\n]/u.test(value) ? `"${value.replace(/"/gu, '""')}"` : value;
}

function rowValues<Row>(sheet: ExportSheet<Row>, row: Row): string[] {
  return sheet.columns.map((column) => column.exportValue?.(row) ?? '');
}

/** UTF-8 with a BOM so Excel keeps the Azerbaijani letters, CRLF line ends. */
export function buildCsv<Row>(sheet: ExportSheet<Row>): string {
  const lines: string[] = [sheet.columns.map((column) => csvCell(column.header)).join(',')];
  for (const row of sheet.rows) {
    lines.push(rowValues(sheet, row).map(csvCell).join(','));
  }
  if (sheet.totals !== undefined) {
    lines.push(sheet.totals.map(csvCell).join(','));
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/* -------------------------------------------------------------------- xlsx */

function escapeXml(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;');
}

function columnLetter(index: number): string {
  let value = index + 1;
  let letters = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    value = Math.floor((value - 1) / 26);
  }
  return letters;
}

function xmlCell(columnIndex: number, rowIndex: number, value: string, numeric: boolean): string {
  const ref = `${columnLetter(columnIndex)}${rowIndex}`;
  if (numeric && isNumeric(value)) {
    return `<c r="${ref}"><v>${value.trim()}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function sheetXml<Row>(sheet: ExportSheet<Row>): string {
  const rows: string[] = [];
  const headerCells = sheet.columns
    .map((column, index) => xmlCell(index, 1, column.header, false))
    .join('');
  rows.push(`<row r="1">${headerCells}</row>`);

  sheet.rows.forEach((row, rowIndex) => {
    const number = rowIndex + 2;
    const cells = sheet.columns
      .map((column, index) => xmlCell(index, number, column.exportValue?.(row) ?? '', column.numeric === true))
      .join('');
    rows.push(`<row r="${number}">${cells}</row>`);
  });

  if (sheet.totals !== undefined) {
    const number = sheet.rows.length + 2;
    const cells = sheet.totals
      .map((value, index) => xmlCell(index, number, value, sheet.columns[index]?.numeric === true))
      .join('');
    rows.push(`<row r="${number}">${cells}</row>`);
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rows.join('')}</sheetData></worksheet>`;
}

function sanitizeSheetName(value: string): string {
  const cleaned = value.replace(/[[\]:*?/\\]/gu, ' ').trim();
  return (cleaned === '' ? 'Sheet1' : cleaned).slice(0, 31);
}

/* --------------------------------------------------------------------- zip */

const CRC_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = (CRC_TABLE[(crc ^ byte) & 0xff] as number) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/** A zip with stored entries: enough for an OOXML package, no compressor needed. */
function buildZip(entries: readonly ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const local = new Uint8Array(30 + name.length + entry.data.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, entry.data.length, true);
    localView.setUint32(22, entry.data.length, true);
    localView.setUint16(26, name.length, true);
    localView.setUint16(28, 0, true);
    local.set(name, 30);
    local.set(entry.data, 30 + name.length);
    locals.push(local);

    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, entry.data.length, true);
    centralView.setUint32(24, entry.data.length, true);
    centralView.setUint16(28, name.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    central.set(name, 46);
    centrals.push(central);

    offset += local.length;
  }

  const centralSize = centrals.reduce((sum, entry) => sum + entry.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);
  endView.setUint16(20, 0, true);

  const total = offset + centralSize + end.length;
  const output = new Uint8Array(total);
  let cursor = 0;
  for (const part of [...locals, ...centrals, end]) {
    output.set(part, cursor);
    cursor += part.length;
  }
  return output;
}

export function buildXlsx<Row>(sheet: ExportSheet<Row>): Uint8Array {
  const encoder = new TextEncoder();
  const name = sanitizeSheetName(sheet.sheetName);
  const parts: ZipEntry[] = [
    {
      name: '[Content_Types].xml',
      data: encoder.encode(
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>',
      ),
    },
    {
      name: '_rels/.rels',
      data: encoder.encode(
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>',
      ),
    },
    {
      name: 'xl/workbook.xml',
      data: encoder.encode(
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${escapeXml(name)}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
      ),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: encoder.encode(
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>',
      ),
    },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheetXml(sheet)) },
  ];
  return buildZip(parts);
}

/** Browser-only: hands the sheet to the user as a file. */
export function downloadSheet<Row>(sheet: ExportSheet<Row>, format: ExportFormat): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return;
  }
  let blob: Blob;
  if (format === 'csv') {
    blob = new Blob([buildCsv(sheet)], { type: 'text/csv;charset=utf-8' });
  } else {
    const bytes = buildXlsx(sheet);
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = exportFileName(sheet.sheetName, format);
  anchor.click();
  URL.revokeObjectURL(url);
}
